/* ── 213B §9 [RULED 42] — WHERE A POP-OUT STANDS: ON THE SCREEN THE APP IS ON ──
 *
 * Your words, 29/09: « opening the popout repositions it in middle of screen,
 * should be on far right of any screen ». The asked place was
 * `screen.availWidth − width − 20`: `availWidth` is the app's screen's width,
 * but `left` is a desktop coordinate, so on a second screen to the right the
 * window landed on the first. And a chat (ruling 21, 209 §27) stood « beside the
 * column, to its left » even when no column was open — mid-screen.
 *
 * THE SCREEN IS THE APP WINDOW'S OWN: its left edge on the desktop
 * (`screen.availLeft`, which Safari and Chrome expose and TypeScript's lib.dom
 * does not name) plus its width. Pure arithmetic over a given geometry, so a
 * recording runs it over one screen and a second screen to the right
 * (recordings/213b-popout-place). Where a browser moves or ignores the asked
 * place, the 74m trace (App.tsx `_popTrace`) compares asked and given — that
 * part is Laurent's glance on his screens. */

/** What the arithmetic needs of a screen: its left edge on the desktop (absent on an engine without it → 0) and its width. */
export interface ScreenGeom {
  availLeft?: number | null;
  availWidth: number;
}

/** The app window's own screen, read off `window.screen`. */
export function appScreen(win: Window): ScreenGeom {
  const sc = win.screen as Screen & { availLeft?: unknown };
  const left = typeof sc.availLeft === "number" && Number.isFinite(sc.availLeft) ? sc.availLeft : 0;
  return { availLeft: left, availWidth: sc.availWidth };
}

/** `left` for a window of `width` standing `margin` px in from the RIGHT edge of that screen — the pop-outs' default
 *  (the column's, a chat's with no column open). */
export function rightEdgeLeft(g: ScreenGeom, width: number, margin = 20): number {
  return (g.availLeft ?? 0) + g.availWidth - width - margin;
}

/** A chat's `left`: beside the column's window, to its left, when that window is open (`colLeft` is where it stands);
 *  else at the right edge of the app's screen. Never left of that screen's own edge. */
export function chatLeft(g: ScreenGeom, width: number, colLeft: number | null): number {
  if (colLeft == null) return rightEdgeLeft(g, width);
  return Math.max(g.availLeft ?? 0, colLeft - width - 12);
}

/** The column's window's left edge on the desktop when that window is open, else null (the handle is untyped in App.tsx). */
export function popOutLeftOf(win: unknown): number | null {
  const w = win as { closed?: unknown; screenX?: unknown } | null | undefined;
  return w && w.closed !== true && typeof w.screenX === "number" ? w.screenX : null;
}
